import { IState } from "..";
import { createSlice } from "@reduxjs/toolkit";
import { AppThunk } from "../../app/store";
import SubAccount, { SubAccountMinimal } from "../../app/data/account/SubAccount";
import {
  BolUploadRequestModel,
  PickupRequestModel,
  PickupsResponseContentModel,
  QuickPickupCreateModel
} from "../../app/data/pickup/models";
import BolService from "../../app/data/bol/bolService";
import PickupService from "../../app/data/pickup/pickupService";
import CompanyService from "../../app/data/company/companyService";
import TrackingService from "../../app/data/tracking/trackingService";
import { initialPickupState } from "./PickupState";

const bolService = BolService.getInstance();
const companyService = CompanyService.getInstance();
const pickupService = PickupService.getInstance();
const trackingService = TrackingService.getInstance();

let responseRecognizedDocId: any;
export const pickupSlice = createSlice({
  name: "pickup",
  initialState: initialPickupState,
  reducers: {
    resetPickupState: (state) => initialPickupState,
    setTeamName: (state, { payload }) => {
      state.teamName = payload;
    },
    setShipper: (state, { payload }) => {
      state.teamId = payload.teamId;
      state.shipper.id = payload.shipper.id;
      state.shipper.accountNumber = Number(payload.shipper.accountNumber);
      state.shipper.name = payload.shipper.name;
      state.shipper.address = {
        address1: payload.shipper.address?.address1,
        city: payload.shipper.address?.city,
        postalCode: payload.shipper.address?.postalCode,
        state: payload.shipper.address?.state,
        additionalAddressLine: payload.shipper.address?.additionalAddressLine,
      };
      state.bolNumber = payload.bolNumber;
      state.poNumber = payload.poNumber;
      state.additionalReference = payload.additionalReference;
      state.specialInstructions = payload.specialInstructions;
      state.pickupDate = payload.pickupDate;
      state.pickupReadyTime = payload.pickupReadyTime;
      state.pickupCloseTime = payload.pickupCloseTime;
      state.shipper.contacts = {
        firstName: payload.shipper.contacts.firstName,
        lastName: payload.shipper.contacts.lastName,
        mobilePhone: payload.shipper.contacts.mobilePhone,
        allowSms: payload.shipper.contacts.allowSms,
        email: payload.shipper.contacts.email
      };
    },
    setConsignee: (state, { payload }) => {
      state.consignee.name = payload.name;
      state.consignee.address.address1 = payload.address?.address1;
      state.consignee.address.city = payload.address?.city;
      state.consignee.address.postalCode = payload.address?.postalCode;
      state.consignee.address.state = payload.address?.state;
      state.consignee.address.additionalAddressLine = payload.address?.additionalAddressLine;
      state.consignee.phone = payload.phone;
      state.consignee.email = payload.email;
    },
    addItem: (state, { payload }) => {
      state.items.push({
        packageType: payload.packageType,
        units: payload.units ? parseFloat(payload.units) : undefined,
        yards: payload.yards ? parseFloat(payload.yards) : undefined,
        weight: payload.weight ? parseFloat(payload.weight) : undefined,
        length: payload.length ? parseFloat(payload.length) : undefined,
        width: payload.width ? parseFloat(payload.width) : undefined,
        height: payload.height ? parseFloat(payload.height) : undefined,
        diameter: payload.diameter ? parseFloat(payload.diameter) : undefined,
        ratingClass: payload.ratingClass,
        sidemark: payload.sidemark,
        notes: payload.notes,
        deleted: false,
        commodityCode: payload.commodityCode,
      });
    },
    updateItem: (state, { payload }) => {
      const i = payload.index;
      state.items[i].packageType = payload.data.packageType;
      state.items[i].units = payload.data.units ? parseFloat(payload.data.units) : undefined;
      state.items[i].yards = payload.data.yards ? parseFloat(payload.data.yards) : undefined;
      state.items[i].weight = payload.data.weight ? parseFloat(payload.data.weight) : undefined;
      state.items[i].length = payload.data.length ? parseFloat(payload.data.length) : undefined;
      state.items[i].width = payload.data.width ? parseFloat(payload.data.width) : undefined;
      state.items[i].height = payload.data.height ? parseFloat(payload.data.height) : undefined;
      state.items[i].diameter = payload.data.diameter ? parseFloat(payload.data.diameter) : undefined;
      state.items[i].ratingClass = payload.data.ratingClass;
      state.items[i].sidemark = payload.data.sidemark;
      state.items[i].notes = payload.data.notes;
      state.items[i].commodityCode = payload.data.commodityCode;
    },
    setItems: (state, { payload }) => {
      state.items = payload;
    },
    updatePickupNumber: (state, { payload }) => {
      state.pickupNumber = payload.pickupNumber;
    },
    deleteItem: (state, { payload }) => {
      state.items[payload].deleted = true;
    },
    clearDeletedItems: (state) => {
      state.items = state.items.filter(item => !item.deleted);
    },
    clearItems: (state) => {
      state.items = [];
    },
    requestStarted: (state, { payload }) => {
      state.requestStarted = true;
      state.requestSucceed = false;
      state.requestFailed = false;
      state.requestCreator = payload;
      state.requestError = "";
    },
    requestSucceed: (state) => {
      state.requestStarted = false;
      state.requestSucceed = true;
      state.requestFailed = false;
    },
    requestFailed: (state, { payload }) => {
      state.requestStarted = false;
      state.requestSucceed = false;
      state.requestFailed = true;
      state.requestError = payload;
    },
    clearRequestError: (state) => {
      state.requestFailed = false;
      state.requestError = "";
    },
    setCompanies: (state, { payload }) => {
      state.companies = [];
      state.companies.push(...payload.map((company: any) => {
        return {
          label: company.name,
          value: company.id,
          valueForSearch: company.name
        }
      }));
    },
    clearShippers: (state) => {
      state.shippers = null;
      state.shippersSearch = null;
    },
    setShippers: (state, { payload }) => {
      state.shippers = payload;
    },
    setShippersSearch: (state, { payload }) => {
      state.shippersSearch = payload;
    },
    addShipper: (state, { payload }) => {
      state.shippers?.push(payload);
      state.shippersSearch?.push(payload);
    },
    // updateShipper: () => {
    //   const index = state.shippers.findIndex(shipper.accountNumber === paymentLoadingFailed.accountNumber)
    // },
    setConsignees: (state, { payload }) => {
      state.consignees = payload;
    },
    setConsigneeCheckResult: (state, { payload }) => {
      state.consigneeCheckResult = payload;
    },
    setConsigneeZipChecked: (state, { payload }) => {
      state.consigneeZipChecked = payload;
    },
    setPaypointNumber: (state, { payload }) => {
      state.paypointNumber = payload;
    },
    setPickupId: (state, { payload }) => {
      state.pickupId = payload;
    },
    setRecognizedBolData: (state, { payload }) => {
      state.recognizedBolData = payload;
    },
    setBolPreview: (state, { payload }) => {
      state.bolPreviewType = payload.type;
      state.bolPreviewBlob = payload.file;
    },
    clearBolData: (state) => {
      state.bolNumber = "";
      state.poNumber = "";
      state.specialInstructions = "";
      state.items = [];
      state.shipper = { ...initialPickupState.shipper };
      state.consignee = { ...initialPickupState.consignee };
      state.recognizedBolData = null;
      state.bolPreviewBlob = null;
      state.bolPreviewType = "";
      state.populatedFromBol = false;
    },
    populatePickupFromBol: (state, { payload }) => {
      state.bolNumber = payload.bolNumber;
      state.poNumber = payload.poNumber;
      state.specialInstructions = payload.specialInstructions;

      state.shipper.name = payload.shipFrom.company;
      state.shipper.address.address1 = payload.shipFrom.address.street;
      state.shipper.address.city = payload.shipFrom.address.city;
      state.shipper.address.state = payload.shipFrom.address.state;
      state.shipper.address.postalCode = payload.shipFrom.address.zipcode;
      state.shipper.contacts.email = payload.shipFrom.contact.email;
      if (payload.shipFrom.contact.phoneNumber) state.shipper.contacts.mobilePhone = payload.shipFrom.contact.phoneNumber.formatPhone();
      [state.shipper.contacts.firstName, state.shipper.contacts.lastName] = (payload.shipFrom.contact?.name ?? "").trim().split(/ (.+)/);

      state.consignee.name = payload.shipTo.company;
      state.consignee.address.address1 = payload.shipTo.address.street;
      state.consignee.address.city = payload.shipTo.address.city;
      state.consignee.address.state = payload.shipTo.address.state;
      state.consignee.address.postalCode = payload.shipTo.address.zipcode;
      if (payload.shipTo.contact.phoneNumber) state.consignee.phone = payload.shipTo.contact.phoneNumber.formatPhone();
      state.consignee.email = payload.shipTo.contact.email;

      for (const item of payload.items) {
        state.items.push({
          packageType: item.type,
          units: item.numberOfHandlingUnits,
          yards: item.squareYards,
          weight: item.weight,
          length: item.dimensions.length,
          width: item.dimensions.width,
          height: item.dimensions.height,
          description: item.description,
          notes: item.notes,
          ratingClass: item.class,
          deleted: false
        });
      }

      state.populatedFromBol = true;
    }
  }
});

export const {
  addItem,
  clearDeletedItems,
  clearItems,
  clearRequestError,
  clearShippers,
  deleteItem,
  requestFailed,
  requestStarted,
  requestSucceed,
  resetPickupState,
  setCompanies,
  setConsignee,
  setConsigneeCheckResult,
  setConsignees,
  setConsigneeZipChecked,
  setShipper,
  setShippers,
  setItems,
  setPickupId,
  setShippersSearch,
  addShipper,
  setTeamName,
  setPaypointNumber,
  updateItem,
  updatePickupNumber,
  setRecognizedBolData,
  setBolPreview,
  clearBolData,
  populatePickupFromBol
} = pickupSlice.actions;

export const pickupSelector = (state: IState) => {
  return state.pickup;
};

export const searchCompany = (
  value: string
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("SEARCH_COMPANY"));
  const response = await trackingService.searchTeams(value);
  if (response.ok()) {
    dispatch(setCompanies(response.data));
    dispatch(requestSucceed());
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  }
};

export const getShippers = (
  teamId: string,
  onSuccess?: (shippers: SubAccountMinimal[]) => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("GET_SHIPPERS"));
  const response1 = await companyService.getAllAccounts(teamId);
  const response2 = await companyService.getAllExtraShippers(teamId);
  if (response1.ok() || response2.ok()) {
    const shippers = [
      ...(response1.data && response1.data.content.filter((obj: SubAccount) => obj.shipper)),
      ...(response2.data && response2.data?.content)
    ];
    dispatch(setShippers(shippers));
    dispatch(setPaypointNumber(response1.data && response1.data.content.find((obj: SubAccount) => obj.paypoint)?.accountNumber));
    dispatch(requestSucceed());
    onSuccess && onSuccess(shippers);
  } else {
    dispatch(requestFailed(response1.getError
      ? response1.getError()
      : (response2.getError
        ? response2.getError()
        : "Error")
    ));
  }
};

export const getConsignees = (): AppThunk => async (dispatch) => {
  // dispatch(requestStarted("GET_CONSIGNEES"));
  // const response = await pickupService.getConsignees();
  // if (response.ok()) {
  //   dispatch(requestSucceed());
  //   dispatch(setConsignees(response.data));
  // } else {
  //   dispatch(requestFailed(response.getError ? response.getError() : "Error"));
  // }
};

export const checkPickup = (
  zip: string,
  onSuccess: () => void,
  onFailed: () => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("CHECK_PICKUP"));
  const response = await bolService.checkPickup(zip);
  if (response.ok()) {
    dispatch(requestSucceed());
    onSuccess();
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
    onFailed();
  }
};

export const createPickup = (
  request: PickupRequestModel,
  callback?: (error?: any, response?: any) => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("CREATE_PICKUP"));
  try {
    const response = await pickupService.createPickup(request);

    if (response.ok()) {
      dispatch(requestSucceed());
      dispatch(updatePickupNumber(response.data));

      try {
        const convertPickupRequest = (oldRequest: any): any => {
          const formatTime = (time: string) => {
            // convert "9:00 am" to "09:00"
            if (!time) return "";
            const [hourMin, modifier] = time.split(" ");
            let [hour, min] = hourMin.split(":").map(Number);
            if (modifier?.toLowerCase() === "pm" && hour !== 12) hour += 12;
            if (modifier?.toLowerCase() === "am" && hour === 12) hour = 0;
            return `${hour.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}`;
          };

          return {
            recognizedDocId: responseRecognizedDocId, // static or generate dynamically
            bolNumber: oldRequest.bolNumber || "",
            poNumber: oldRequest.poNumber || "",
            specialInstructions: oldRequest.specialInstructions || "",
            shipFrom: {
              company: oldRequest.shipper?.name || "",
              address: {
                street: oldRequest.shipper?.address?.address1 || "",
                city: oldRequest.shipper?.address?.city || "",
                state: oldRequest.shipper?.address?.state || "",
                zipcode: oldRequest.shipper?.address?.postalCode || ""
              },
              contact: {
                name: oldRequest.shipper?.contacts?.firstName || "",
                phoneNumber: oldRequest.shipper?.contacts?.mobilePhone || "",
                email: oldRequest.shipper?.contacts?.email || ""
              }
            },
            pickupWindow: {
              date: oldRequest.pickupDate || "",
              startTime: formatTime(oldRequest.pickupReadyTime),
              endTime: formatTime(oldRequest.pickupCloseTime)
            },
            shipTo: {
              company: oldRequest.consignee?.name || "",
              address: {
                street: oldRequest.consignee?.address?.address1 || "",
                city: oldRequest.consignee?.address?.city || "",
                state: oldRequest.consignee?.address?.state || "",
                zipcode: oldRequest.consignee?.address?.postalCode || ""
              },
              contact: {
                name: "", // original request does not have firstName for consignee contact
                phoneNumber: oldRequest.consignee?.phone || "",
                email: oldRequest.consignee?.email || ""
              },
              receivingHours: {
                startTime: formatTime(oldRequest.pickupReadyTime),
                endTime: formatTime(oldRequest.pickupCloseTime)
              }
            },
            items: oldRequest.items?.map((item: any) => ({
              type: item.packageType,
              numberOfHandlingUnits: item.units,
              weight: item.weight,
              pieces: item.units, // assuming pieces = units
              freightClass: "", // no freightClass in old request
              description: item.description,
              notes: item.notes || "",
              squareYards: item.yards || 0,
              rollNumber: "", // optional
              dimensions: {
                length: item.length || 0,
                width: item.width || 0,
                height: item.height || 0
              }
            })) || []
          };
        };
        const feedbackRequest = convertPickupRequest(request);

        await pickupService.pickupFeedback(feedbackRequest);
      } catch (feedbackErr: any) {
        console.error("Pickup feedback failed:", feedbackErr?.message || feedbackErr);
      }

      callback?.(null, response.data);
    } else {
      const error = response.getError ? response.getError() : "Error";
      dispatch(requestFailed(error));
      callback?.(error, null);
    }
  } catch (err: any) {
    dispatch(requestFailed(err.message || "Error"));
    callback?.(err, null);
  }
};

export const editPickup = (
  request: PickupRequestModel,
  onSuccess?: (pickup: PickupsResponseContentModel) => void,
  onFailed?: (error: string | {} | null) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("EDIT_PICKUP"));
  const response = await pickupService.editPickup(request);
  if (response.ok()) {
    dispatch(requestSucceed());
    dispatch(updatePickupNumber(response.data));
    onResult?.(undefined, response.data); // Pass the actual data
  } else {
    const error = response.getError ? response.getError() : "Error";
    dispatch(requestFailed(error));
    onResult?.(error, undefined); // Pass error
  }
};

export const checkConsignee = (
  zip: string,
  onSuccess: () => void,
  onNotFound: () => void,
  onFailed: () => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("CHECK_CONSIGNEE_ZIP"));
  const response = await bolService.checkConsignee(zip);
  if (response.ok()) {
    dispatch(requestSucceed());
    dispatch(setConsigneeCheckResult(true));
    dispatch(setConsigneeZipChecked(zip));
    onSuccess();
  } else {
    const error = response.getError ? response.getError() : "Error";
    dispatch(requestFailed(error));
    dispatch(setConsigneeCheckResult(false));
    dispatch(setConsigneeZipChecked(zip));
    if (response.status === 404) {
      onNotFound();
    } else {
      onFailed();
    }
  }
};

export const uploadBol = (
  request: BolUploadRequestModel,
  originalFile: string,
  onSuccess: () => void,
  onFail: (err: any) => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("UPLOAD_BOL"));
  const response = await pickupService.uploadBol(request);
  if (response.ok()) {
    dispatch(requestSucceed());
    dispatch(setRecognizedBolData(response.data));
    dispatch(populatePickupFromBol(response.data));
    dispatch(setBolPreview({
      type: request.fileType,
      file: originalFile
    }));
    onSuccess && onSuccess();
    responseRecognizedDocId = response.data.recognizedDocId;
  } else {
    dispatch(requestFailed(response.getError ? response.getError() : "Error"));
    onFail(response.getError ? response.getError() : "Error");
  }
};

export const afterUploadPickupBOL = (
  pickupId: string,
  file: File,
  onSuccess?: () => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("UPLOAD_PICKUP_BOL"));

  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await pickupService.afterUploadPickupBOL(pickupId, formData);

    if (response.ok()) {
      dispatch(requestSucceed());
      onSuccess && onSuccess();
    } else {
      dispatch(requestFailed(response.getError ? response.getError() : "Failed to upload BOL"));
    }
  } catch (error) {
    dispatch(requestFailed(error instanceof Error ? error.message : "Unknown error"));
  }
};

export const createQuickPickup = (
  request: QuickPickupCreateModel,
  onSucccess?: (pickupNumber?: string) => void,
  onError?: (error?: any) => void
): AppThunk => async (dispatch) => {
  dispatch(requestStarted("CREATE_QUICK_PICKUP"));
  try {
    const response = await pickupService.createQuickPickup(request);

    if (response.ok()) {
      dispatch(requestSucceed());
      dispatch(resetPickupState());
      onSucccess && onSucccess(response.data.pickupNumber);
    } else {
      const error = response.getError ? response.getError() : "Error";
      dispatch(requestFailed(error));
      onError && onError(error);
    }
  } catch (err: any) {
    dispatch(requestFailed(err.message || "Error"));
    onError && onError(err);
  }
};

const pickupReducer = pickupSlice.reducer;
export default pickupReducer;